import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, RotateCcw, Loader2, Mail, FileText, Info } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const TAB_ORDER = ["email", "pdf"];

export default function TemplatesSettings() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("settings.templates.write");
  const canReset = hasPermission("users.create"); // super_admin proxy
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const activeTab = TAB_ORDER.includes(params.get("tab")) ? params.get("tab") : "email";

  const [meta, setMeta] = useState(null); // {templates, keys, allowed_variables}
  const [draft, setDraft] = useState({});
  const [previews, setPreviews] = useState({}); // key → rendered string
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/templates");
      setMeta(data);
      setDraft(data.templates);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => {
    if (!meta) return false;
    return Object.keys(draft).some((k) => draft[k] !== meta.templates[k]);
  }, [draft, meta]);

  const setDraftValue = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const previewOne = useCallback(async (key) => {
    try {
      const { data } = await api.post("/settings/templates/preview", {
        template_key: key,
        template: draft[key] ?? "",
      });
      setPreviews((p) => ({ ...p, [key]: data.rendered }));
    } catch (err) {
      setPreviews((p) => ({ ...p, [key]: `Preview error: ${formatApiError(err)}` }));
    }
  }, [draft]);

  // Debounced live preview per key on draft change.
  const timerRef = useRef({});
  useEffect(() => {
    if (!meta) return;
    Object.keys(meta.keys).forEach((key) => {
      clearTimeout(timerRef.current[key]);
      timerRef.current[key] = setTimeout(() => previewOne(key), 350);
    });
    return () => Object.values(timerRef.current).forEach(clearTimeout);
  }, [draft, meta, previewOne]);

  const handleSave = async () => {
    if (!canEdit || !dirty) return;
    // Only send changed keys.
    const payload = {};
    for (const k of Object.keys(draft)) {
      if (draft[k] !== meta.templates[k]) payload[k] = draft[k];
    }
    if (Object.keys(payload).length === 0) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/settings/templates", payload);
      setMeta(data);
      setDraft(data.templates);
      toast.success("Templates saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleResetField = async (key) => {
    if (!canEdit) return;
    const dflt = meta?.keys?.[key]?.default;
    if (dflt === undefined) return;
    setDraftValue(key, dflt);
  };

  const handleResetAll = async () => {
    if (!canReset) return;
    if (!window.confirm("Reset all templates to the seeded defaults? Unsaved changes will be lost.")) return;
    try {
      const { data } = await api.post("/settings/templates/reset", { keys: "all" });
      setMeta(data);
      setDraft(data.templates);
      toast.success("Templates reset to defaults");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading || !meta) {
    return (
      <div className="p-6" data-testid="settings-templates-loading">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const groupedKeys = { email: [], pdf: [] };
  for (const k of Object.keys(meta.keys)) {
    const group = k.split(".")[0];
    if (groupedKeys[group]) groupedKeys[group].push(k);
  }

  return (
    <div className="p-6 space-y-6 pb-24" data-testid="settings-templates-page">
      <PageHeader
        testId="settings-templates-header"
        title="Email & PDF Templates"
        subtitle="Edit the subject line, body, and report wording used by outgoing emails and generated PDFs."
        actions={
          <Button variant="outline" onClick={() => navigate("/settings")} data-testid="settings-templates-back">
            Back to Settings
          </Button>
        }
      />

      <Tabs value={activeTab} onValueChange={(t) => setParams({ tab: t })}>
        <TabsList className="rounded-lg bg-muted p-1">
          <TabsTrigger value="email" data-testid="settings-templates-tab-email" className="gap-2">
            <Mail className="h-4 w-4" /> Email
          </TabsTrigger>
          <TabsTrigger value="pdf" data-testid="settings-templates-tab-pdf" className="gap-2">
            <FileText className="h-4 w-4" /> PDF
          </TabsTrigger>
        </TabsList>

        {TAB_ORDER.map((group) => (
          <TabsContent key={group} value={group} className="mt-4 space-y-4" data-testid={`settings-templates-tab-content-${group}`}>
            {groupedKeys[group].map((key) => {
              const cfg = meta.keys[key];
              return (
                <TemplateFieldCard
                  key={key}
                  fieldKey={key}
                  cfg={cfg}
                  value={draft[key] ?? ""}
                  onChange={(v) => setDraftValue(key, v)}
                  onReset={() => handleResetField(key)}
                  rendered={previews[key]}
                  allowedVariables={meta.allowed_variables}
                  canEdit={canEdit}
                  isDirty={draft[key] !== meta.templates[key]}
                />
              );
            })}
          </TabsContent>
        ))}
      </Tabs>

      {canEdit && (
        <div className="fixed bottom-0 left-56 right-0 bg-background border-t border-border p-3 flex justify-between items-center z-30" data-testid="settings-templates-actions-bar">
          <div className="text-xs text-muted-foreground">
            {dirty ? "Unsaved changes" : "All saved"}
          </div>
          <div className="flex gap-2">
            {canReset && (
              <Button variant="outline" onClick={handleResetAll} data-testid="settings-templates-reset-all-btn">
                <RotateCcw className="h-4 w-4" /> Reset all
              </Button>
            )}
            <Button variant="outline" onClick={() => { setDraft(meta.templates); }} disabled={!dirty} data-testid="settings-templates-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!dirty || saving} data-testid="settings-templates-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              <span className="ml-1">Save changes</span>
            </Button>
          </div>
        </div>
      )}

      {!canEdit && (
        <div className="text-xs text-muted-foreground italic" data-testid="settings-templates-readonly-notice">
          You have read-only access to the template editor.
        </div>
      )}
    </div>
  );
}

function TemplateFieldCard({ fieldKey, cfg, value, onChange, onReset, rendered, allowedVariables, canEdit, isDirty }) {
  const inputRef = useRef(null);

  const insertVariable = (varName) => {
    if (!canEdit) return;
    const el = inputRef.current;
    const insertion = `{${varName}}`;
    if (!el) {
      onChange((value || "") + insertion);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.substring(0, start) + insertion + value.substring(end);
    onChange(next);
    // Restore caret after React re-renders.
    requestAnimationFrame(() => {
      if (inputRef.current) {
        const pos = start + insertion.length;
        inputRef.current.focus();
        try { inputRef.current.setSelectionRange(pos, pos); } catch { /* input types without selection */ }
      }
    });
  };

  const isTextarea = cfg.kind === "textarea";

  return (
    <Card className="rounded-md" data-testid={`settings-templates-card-${fieldKey}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-medium">
          {cfg.label} {isDirty && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-warning">Modified</span>}
        </CardTitle>
        {canEdit && (
          <Button variant="ghost" size="sm" onClick={onReset} data-testid={`settings-templates-reset-${fieldKey}`}>
            <RotateCcw className="h-3.5 w-3.5" /> Reset to default
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-xs text-muted-foreground flex items-start gap-2">
          <Info className="h-3 w-3 mt-0.5 shrink-0" /> {cfg.hint}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Template source
            </Label>
            {isTextarea ? (
              <textarea
                ref={inputRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={!canEdit}
                data-testid={`settings-templates-input-${fieldKey}`}
                className="w-full min-h-[140px] rounded-sm border border-border bg-background px-3 py-2 font-mono text-xs leading-relaxed focus:outline-none focus:border-primary disabled:bg-muted/50"
                rows={cfg.max_length > 4000 ? 10 : 6}
              />
            ) : (
              <Input
                ref={inputRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={!canEdit}
                data-testid={`settings-templates-input-${fieldKey}`}
                className="font-mono text-xs"
              />
            )}
            <div className="text-[10px] text-muted-foreground text-right">
              {value.length} / {cfg.max_length}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Live preview
            </Label>
            {fieldKey.startsWith("email.report_body_html") ? (
              <div
                className="min-h-[140px] rounded-sm border border-border bg-muted/30 px-3 py-2 text-xs overflow-auto"
                data-testid={`settings-templates-preview-${fieldKey}`}
                dangerouslySetInnerHTML={{ __html: rendered || "" }}
              />
            ) : (
              <pre
                className="min-h-[140px] rounded-sm border border-border bg-muted/30 px-3 py-2 text-xs whitespace-pre-wrap font-sans overflow-auto"
                data-testid={`settings-templates-preview-${fieldKey}`}
              >
                {rendered || " "}
              </pre>
            )}
          </div>
        </div>

        {canEdit && (
          <div className="space-y-1.5">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Insert variable
            </Label>
            <div className="flex flex-wrap gap-1.5" data-testid={`settings-templates-chips-${fieldKey}`}>
              {allowedVariables.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => insertVariable(v)}
                  className="text-[10px] font-mono px-2 py-0.5 rounded-sm border border-border hover:border-primary hover:text-primary transition-colors"
                  data-testid={`settings-templates-chip-${fieldKey}-${v}`}
                >
                  {"{" + v + "}"}
                </button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
